import { useState } from 'react'
import { formatEther } from 'viem'
import { toast } from 'react-toastify'
import Pending from '@/assets/icons/Pending.svg?react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Disconnect } from '@/components/Disconnect'
import { Skeleton } from '@/components/ui/Skeleton'
import { usePepperBalance } from '@/hooks/pepper-contract/usePepperBalance'
import { useCurrencyFormatter } from '@/hooks/helpers/useCurrencyFormatter'
import { usePepperContractAddress } from '@/hooks/pepper-contract/usePepperContract'
import { useWriteStaking2aContract } from '@/hooks/staking2a-contract/useWriteStaking2aContract'
import { useStaking2aUserStakeDataWithoutLockStatus } from '@/hooks/staking2a-contract/useStaking2aUserStakeData'
import { waitForTransactionReceipt } from '@/utils/wagmi/waitForTransactionReceipt'
import { cn } from '@/lib/utils'
import { logger } from '@/observability/logger'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import { useTracking } from '@/providers/TrackingProvider'

export function BalanceCard({ className }: { className?: string }) {
	const { data: pepperBalance = 0n, isPending: isPendingBalance, refetch: refetchPepperBalance } = usePepperBalance()
	const pepperAddress = usePepperContractAddress()
	const format = useCurrencyFormatter()
	const writeStaking2aContract = useWriteStaking2aContract()
	const { data: userStakedDataWithoutLockStatus, refetch: refetchUserStakedDataWithoutLockStatus } =
		useStaking2aUserStakeDataWithoutLockStatus()
	const { sendEvent } = useTracking()

	const [isClaiming, setIsClaiming] = useState(false)

	const balancIs = format(formatEther(pepperBalance))
	const { totalPendingUnstake: totalClaimable = 0n } = userStakedDataWithoutLockStatus ?? {}

	const handleClaim = async () => {
		try {
			sendEvent('click_claim_pepper')
			setIsClaiming(true)
			const hash = await writeStaking2aContract({
				functionName: 'claim',
				args: [pepperAddress]
			})

			await waitForTransactionReceipt({ hash })
			sendEvent('claim_pepper_successful', { txHash: hash })
			refetchUserStakedDataWithoutLockStatus()
			refetchPepperBalance()
		} catch (error) {
			logger.error(error, 'Error claiming Pepper')
			toast.error('An error occurred while claiming Pepper.')
		} finally {
			setIsClaiming(false)
		}
	}

	return (
		<Card className={cn('p-6 flex flex-col items-center justify-between gap-3', className)}>
			<div className="flex flex-col items-center">
				<img src="/images/balance.svg" alt="balance" className="size-[60px] mb-4" />
				<h4 className="text-2xl leading-none uppercase">My PEPPER Balance</h4>
				{isPendingBalance ? (
					<Skeleton className="w-[13ch] h-[30px]" />
				) : (
					<span className="text-[2.75rem] md:text-[3.75rem] leading-none ">{balancIs}</span>
				)}
				{totalClaimable > 0 && (
					<span className="flex items-center gap-2 text-neutral-40 leading-none text-[1.75rem]">
						<Pending width={16} height={16} />
						{`${format(formatEther(totalClaimable ?? 0n))} claimable`}
					</span>
				)}
			</div>
			<div className="flex gap-3">
				{totalClaimable > 0n && (
					<Button
						onClick={handleClaim}
						loading={isClaiming}
						variant="danger"
						className="leading-none py-5 px-10"
						data-testid={testIdFactory.pepperClaimButton()}
					>
						Claim
					</Button>
				)}
				<Disconnect className="py-5 px-10" />
			</div>
		</Card>
	)
}
